import { IHttpResponse } from 'angular';
import { Collection, AddRemoveCollectionItemsData, CollectionError, CollectionItem, DeleteCollectionData } from '../../app/spaces/Collections/CollectionModels';
import { GetCollectionsResponse, AddRemoveItemsResponse, DeleteResponse, GetCollectionItemsResponse } from '../../app/spaces/Collections/Collections';
import { ICollectionInfo } from '../../app/spaces/Collections/ICollectionInfo';
import { ListResult } from '../fetchers/ListResult';
import { MapperHelper } from './MapperHelper';
import { WorkbookMapper } from './WorkbookMapper';
import { ProjectMapper } from './ProjectMapper';
import { DatasourceMapper } from './DatasourceMapper';
import { ViewMapper } from './ViewMapper';
import { FlowMapper } from './FlowMapper';
import { MetricMapper } from './MetricMapper';
import { PublishedConnectionMapper } from './PublishedConnectionMapper';
import { DataRoleMapper } from './DataRoleMapper';
import { IError } from '../ServerApi/types/IError';
import { IResult } from '../ServerApi/types/IResponse';
import { IContentInfo } from '../models/IContentInfo';
import { Set } from '../models/Set';

export module CollectionMapper {
  export function mapsSingle(response: IHttpResponse<Collection>): ICollectionInfo {
    const collection = response.data;
    return {
      luid: collection.luid,
      id: collection.id,
      name: collection.name,
      description: collection.description,
      contentType: 'collection',
      totalItemCount: collection.totalItemCount,
      permissionedItemCount: collection.permissionedItemCount,
      userId: collection.userId,
      visibility: collection.visibility,
      createdAt: MapperHelper.toDate(collection.createdAt),
      updatedAt: MapperHelper.toDate(collection.updatedAt),
      favorite: collection.isFavorite
    };
  }

  export function mapList(response: IHttpResponse<GetCollectionsResponse>, start: number): ListResult<ICollectionInfo> {
    return {
      items: response.data.items.map((item: Collection) => {
        return {
          id: item.id,
          luid: item.luid,
          name: item.name,
          description: item.description,
          contentType: 'collection',
          totalItemCount: item.totalItemCount,
          permissionedItemCount: item.permissionedItemCount,
          userId: item.userId,
          visibility: item.visibility,
          createdAt: MapperHelper.toDate(item.createdAt),
          updatedAt: MapperHelper.toDate(item.updatedAt),
          favorite: item.isFavorite,
        };
      }),
      total: response.data.totalCount,
      index: start
    };
  }

  export function mapFromCRASS(content: any): ICollectionInfo {
    return {
      contentType: 'collection',
      luid: content.luid,
      name: content.name,
      id: content.id,
      userId: content.ownerId,
      totalItemCount: content.itemNames.length,
      permissionedItemCount: -1, // We don't know when fetching collections from crass
      visibility: content.visibility,
      description: content.description,
      createdAt: MapperHelper.toDate(content.createdTime),
      updatedAt: MapperHelper.toDate(content.modifiedTime),
      favorite: false,
      owner: {
        id: content.ownerId + '',
        displayName: content.ownerName || '',
        username: content.ownerAlias || '',
        domainName: content.ownerDomain || '',
        isHiddenInternalUser: false
      }
    };
  }

  export function mapAddRemoveItemsResponse(response: IHttpResponse<AddRemoveItemsResponse>): AddRemoveCollectionItemsData {
    return {
      numItemsSuccessfullyUpdated: response.data.response,
      errors: response.data.errors
    };
  }

  export function mapItemsList(response: IHttpResponse<GetCollectionItemsResponse>, start: number): ListResult<any> {
    return {
      items: response.data.items.map((item: CollectionItem) => {
        if(item.content.contentType==='workbook') {
          return WorkbookMapper.mapFromCRASS(item.content);
        } else if(item.content.contentType==='unifieddatasource') {
          return DatasourceMapper.mapFromCRASS(item.content);
        } else if(item.content.contentType==='view') {
          return ViewMapper.mapFromCRASS(item.content);
        } else if(item.content.contentType==='project') {
          return ProjectMapper.mapFromCRASS(item.content);
        } else if(item.content.contentType==='flow') {
          return FlowMapper.mapFromCRASS(item.content);
        } else if(item.content.contentType==='metric') {
          return MetricMapper.mapFromCRASS(item.content);
        } else if(item.content.contentType==='datarole') {
          return DataRoleMapper.mapFromCRASS(item.content);
        } else if(item.content.contentType==='publishedConnection') {
          return PublishedConnectionMapper.mapFromCRASS(item.content);
        }
        return mapDefault(item);
      }),
      total: response.data.totalCount,
      index: start
    };
  }

  function mapDefault(item: any) {
    return {
      ...item.content,
      luid: item.content.luid,
      id: item.content.syncId,
      contentType: item.content.contentType,
      name: item.content.title,
      tags: [],
      owner: {
          id: 0,
          displayName: item.content.ownerName || '',
          username: item.content.ownerAlias || '',
          domainName: '',
          isHiddenInternalUser: false
      }
    };
  }

  function mapCollectionErrorToIError(collectionError: CollectionError, collection: IContentInfo): IError {
    return {
      code: collectionError.errorCode,
      id: collection.id
    };
  }

  export function mapDeleteCollectionsDataToIResult(data: DeleteCollectionData, collections: Set<IContentInfo>): IResult {
    const mappedErrors = data.errors.map(err => mapCollectionErrorToIError(err, collections.filter((col: any) => err.luid === col.luid).getByIndex(0)));
    return {
      errors: mappedErrors
    };
  }

  export function mapDeleteCollectionsResponse(response: IHttpResponse<DeleteResponse>): DeleteCollectionData {
    return {
      successfulLuids: response.data.response,
      errors: response.data.errors
    };
  }
}
